/**
 * MktgButton — reusable pill CTA button for marketing sections.
 * Applies .mktg-btn-{variant} and .mktg-btn-{size} CSS classes.
 * Hover states are handled entirely by CSS.
 * Wraps in next/link when href is provided.
 *
 * Variants (two only — intentional):
 *   primary      = yellow fill  → main CTA on any light background
 *   secondary    = dark outline → secondary CTA on light background
 *   dark-secondary              → secondary CTA on dark/coloured backgrounds
 */

import React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils/utils';

/** Variant maps to .mktg-btn-{variant} CSS class */
type ButtonVariant = 'primary' | 'secondary' | 'dark-secondary';

/** Size maps to .mktg-btn-{size} CSS class */
type ButtonSize = 'lg' | 'md' | 'sm';

interface MktgButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** When provided the button is wrapped in a next/link anchor */
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  style?: React.CSSProperties;
  'aria-label'?: string;
  children: React.ReactNode;
}

const variantClass: Record<ButtonVariant, string> = {
  primary: 'mktg-btn-primary',
  secondary: 'mktg-btn-secondary',
  'dark-secondary': 'mktg-btn-dark-secondary'
};

const sizeClass: Record<ButtonSize, string> = {
  lg: 'mktg-btn-lg',
  md: 'mktg-btn-md',
  sm: 'mktg-btn-sm'
};

export function MktgButton({
  variant = 'primary',
  size = 'lg',
  href,
  onClick,
  disabled,
  type = 'button',
  className,
  style,
  'aria-label': ariaLabel,
  children
}: MktgButtonProps) {
  const buttonEl = (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(variantClass[variant], sizeClass[size], className)}
      style={style}
    >
      {children}
    </button>
  );

  if (href) {
    return <Link href={href}>{buttonEl}</Link>;
  }

  return buttonEl;
}
